<template>
  <div
    class="fixed top-0 left-0 h-screen w-screen bg-slate-100 overflow-auto scrollbar-lg"
    :style="{ zIndex: 999 }"
  >
    <div class="sticky left-0 top-0" :style="{ zIndex: 49 }">
      <div
        class="px-6 h-10 flex-shrink-0 flex flex-wrap lg:flex-no-wrap items-center justify-between gap-y-2 gap-x-4 bg-pure-white"
      >
        <BlBreadcrumbs :breadcrumbs="breadcrumbs" />
      </div>
      <div
        class="h-14 px-6 flex items-center justify-between bg-pure-white border-b border-slate-400"
      >
        <div class="flex items-center gap-4">
          <BlTextTitle4 text="Reschedule appointment" />
          <div v-if="appointment" class="flex items-center gap-4">
            <div
              class="max-w-80 h-7 px-1 flex items-center gap-1 rounded bg-tulip-100"
              :title="appointment.client.name"
            >
              <BlIcon icon="user-square" color="tulip-7" class="h-6 w-6" />
              <BlTextSubtitle3
                :text="appointment.client.name"
                class="text-tulip-700 truncate"
              />
            </div>
          </div>
        </div>
        <BlInputButton
          type="plain"
          icon="close"
          @clicked="$emit('close-modal')"
        />
      </div>
    </div>

    <div
      v-if="modalLoading"
      class="w-full grid place-items-center"
      :style="{ minHeight: 'calc(100vh - 6rem - 5rem)' }"
    >
      <div class="blum-loader"></div>
    </div>
    <div
      v-else-if="process !== 'select_reschedule'"
      class="w-full py-8 flex flex-col items-center gap-4"
      :style="{ minHeight: 'calc(100vh - 6rem - 5rem)' }"
    >
      <div class="w-152 px-10 flex flex-col">
        <div class="mb-1">
          <BlTextSubtitle1
            v-if="isOrphanAppointment"
            text="Orphan appointment details"
          />
          <BlTextSubtitle1 v-else text="Reschedule appointment" />
        </div>
        <div class="mb-3">
          <div v-if="isOrphanAppointment">
            <BlTextBody2 text="This appointment for" />
            <BlTextBody2 :text="appointment.client.name" class="font-bold" />
            <BlTextBody2 text="no longer has a therapist assigned." />
          </div>
          <div v-else>
            <BlTextBody2
              text="You're about to reschedule the appointment for"
            />
            <BlTextBody2
              :text="`${appointment.client.name}.`"
              class="font-bold"
            />
          </div>
        </div>
        <div class="flex items-center gap-2">
          <div class="w-25 flex-shrink-0 rounded shadow-md">
            <div
              class="h-7 flex items-center justify-center rounded-t bg-tomato-200"
            >
              <BlTextBody2 text="Previous" class="text-tomato-700" />
            </div>
            <div
              class="h-18 flex flex-col items-center justify-center rounded-b bg-pure-white"
            >
              <BlTextTitle4
                :text="displayDate({ date: appointment.date, format: 'DD' })"
                class="text-tomato-700"
              />
              <BlTextBody2
                :text="displayDate({ date: appointment.date, format: 'MMMM' })"
                class="text-tomato-600"
              />
            </div>
          </div>
          <div class="w-90 flex flex-col gap-1 px-2">
            <div class="flex items-center gap-2">
              <BlIcon icon="user" color="slate-6" class="h-4 w-4" />
              <BlTextBody2
                v-if="isOrphanAppointment"
                text="No therapist assigned"
                class="truncate line-through"
              />
              <BlTextBody2
                v-else
                :text="userAppointment.name"
                class="truncate"
              />
            </div>
            <div class="flex items-center gap-2">
              <BlIcon icon="clock-outline" color="slate-6" class="h-4 w-4" />
              <BlTextBody2
                :text="
                  `${appointment.start_time_string} - ${appointment.end_time_string}`
                "
              />
            </div>
            <div v-if="enableBranch" class="flex items-center gap-2">
              <BlIcon icon="location" color="slate-6" class="h-4 w-4" />
              <BlTextBody2
                :text="
                  appointment.room.branch
                    ? appointment.room.branch.name
                    : 'No branch'
                "
              />
            </div>
            <div class="flex items-center gap-2">
              <BlIcon icon="door" color="slate-6" class="h-4 w-4" />
              <BlTextBody2 :text="appointment.room.name" />
            </div>
            <div class="flex items-center gap-2">
              <BlIcon icon="piggy" color="slate-6" class="h-4 w-4" />
              <div>
                <BlTextBody2 :text="`${currentSlotCount} credit(s)`" />
                <BlTextBody2
                  v-if="appointment.credit_transaction_services.length"
                  :text="
                    `of ${appointment.credit_transaction_services[0].package_item_name}`
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isOrphanAppointment"
        class="w-152 py-10 px-10 flex flex-col bg-pure-white rounded-3xl border border-slate-400"
      >
        <div class="mb-3 flex flex-col gap-1">
          <BlTextSubtitle1
            v-if="isRescheduleAsReassign"
            text="Reassign to a new therapist"
          />
          <BlTextSubtitle1 v-else text="Reschedule to a new timeslot" />
          <BlTextBody2
            v-if="isRescheduleAsReassign"
            text="The selected therapist will take over the same appointment slot."
          />
          <BlTextBody2
            v-else
            text="Here's the new timeslot you're rescheduling to:"
          />
        </div>

        <div class="flex flex-col gap-4">
          <div class="flex items-center gap-2">
            <div class="w-25 flex-shrink-0 rounded shadow-md">
              <div
                class="h-7 flex items-center justify-center rounded-t bg-prim-100"
              >
                <BlTextBody2 text="New" class="text-dark-purple-100" />
              </div>
              <div
                class="h-18 flex flex-col items-center justify-center rounded-b bg-pure-white"
              >
                <BlTextTitle4
                  :text="displayDate({ date: dateInput, format: 'DD' })"
                  class="text-dark-purple-100"
                />
                <BlTextBody2
                  :text="displayDate({ date: dateInput, format: 'MMMM' })"
                  class="text-light-purple-400"
                />
              </div>
            </div>
            <div v-if="!therapistInput" class="px-2">
              <BlSelectBox
                class="w-100"
                search
                prefixIcon="user-square"
                v-model="therapistInput"
                placeholder="Select therapist"
                :options="therapistOptions"
                @change="onChangeTherapist"
                :searching="therapistLoading"
                @search="fetchTherapists"
              />
            </div>
            <div v-else class="w-full flex items-center justify-between gap-2">
              <div class="w-90 flex flex-col gap-1 px-2">
                <div
                  class="flex items-center gap-2"
                  :title="therapistInputData.name"
                >
                  <BlIcon icon="user" color="slate-6" class="h-4 w-4" />
                  <BlTextBody2
                    :text="therapistInputData.name"
                    class="truncate"
                  />
                </div>
                <div class="flex items-center gap-2">
                  <BlIcon
                    icon="clock-outline"
                    color="slate-6"
                    class="h-4 w-4"
                  />
                  <BlTextBody2 :text="`${startTimeInput} - ${endTimeInput}`" />
                </div>
                <div
                  v-if="enableBranch && room && room.branch"
                  class="flex items-center gap-2"
                >
                  <BlIcon icon="location" color="slate-6" class="h-4 w-4" />
                  <BlTextBody2
                    :text="room.branch ? room.branch.name : 'No branch'"
                  />
                </div>
                <div class="flex items-center gap-2">
                  <BlIcon icon="door" color="slate-6" class="h-4 w-4" />
                  <div
                    v-if="
                      appointment.room.id === roomInput ||
                        !isRescheduleAsReassign
                    "
                  >
                    <BlTextBody2 :text="room.name" />
                    <BlTextBody2
                      v-if="therapistInputData.is_open_room"
                      text="(open room)"
                    />
                  </div>
                  <div v-else>
                    <BlTextBody2
                      :text="`Move to ${room.name}`"
                      class="font-bold"
                    />
                  </div>
                </div>

                <div
                  v-if="!isRescheduleAsReassign"
                  class="flex items-center gap-2"
                >
                  <BlIcon icon="piggy" color="slate-6" class="h-4 w-4" />
                  <div>
                    <BlTextBody2 :text="`${newSlotCount} credit(s)`" />
                    <BlTextBody2
                      v-if="serviceData"
                      :text="`of ${serviceData.package_item_name}`"
                    />
                  </div>
                </div>
              </div>
              <BlInputButton
                v-if="isRescheduleAsReassign"
                type="outlined"
                icon="edit"
                @clicked="onChangeTherapist(0)"
              />
              <BlInputButton
                v-else
                type="outlined"
                icon="edit"
                @clicked="process = 'select_reschedule'"
              />
            </div>
          </div>
          <div
            v-if="isRescheduleAsReassign && process === 'reschedule'"
            class="py-2 px-3 flex gap-2 bg-cornflower-200 rounded"
          >
            <BlIcon icon="info" color="cornflower-8" class="h-6 w-6" />
            <BlTextBody2
              text="No reschedule credits will be required, as the appointment's date and timeslot remain unchanged."
              class="text-cornflower-800"
            />
          </div>
          <div v-if="isRescheduleAsReassign" class="flex items-center gap-2">
            <BlTextBody2 text="Need to change the timeslot?" />
            <BlInputButton
              type="outlined"
              label="Reschedule appointment"
              dense
              @clicked="process = 'select_reschedule'"
            />
          </div>
          <div v-else class="flex items-center gap-2">
            <BlTextBody2 text="Want to revert to reassigning the therapist?" />
            <BlInputButton
              type="outlined"
              label="Reassign to new therapist"
              dense
              @clicked="onBackToReassign"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isOrphanAppointment && isRescheduleAsReassign"
        class="w-152 py-10 px-10 flex flex-col bg-pure-white rounded-3xl border border-slate-400"
      >
        <div class="mb-3 flex flex-col gap-1">
          <BlTextSubtitle1 text="Select credits for this appointment" />
          <BlTextBody2
            text="Select the source of credits to apply for this appointment."
          />
        </div>
        <div class="flex flex-col gap-4">
          <div>
            <BlSelectBox
              :key="`select-package-item-${therapistInput}`"
              search
              prefixIcon="piggy"
              v-model="packageItemIdInput"
              :placeholder="creditLoading ? 'Loading ...' : 'Select item'"
              :options="packageItemOptions"
              :disabled="!therapistInput"
              @change="packageItemIdInput = $event"
            />
          </div>
          <div class="py-2 px-3 flex flex-col gap-2 bg-cornflower-200 rounded">
            <div class="flex items-center gap-2">
              <BlIcon icon="piggy" color="cornflower-8" class="h-6 w-6" />
              <BlTextSubtitle3
                text="Credits calculation"
                class="text-cornflower-800"
              />
            </div>
            <div class="flex flex-col gap-2">
              <BlTextBody2
                text="Credits will be deducted only after the session ends. However, please be aware of the remaining credits available when creating the appointment."
                class="text-cornflower-800"
              />
              <div class="flex flex-col gap-1">
                <div class="flex items-center justify-between gap-2">
                  <BlTextBody2
                    text="Credits used for this appointment(s)"
                    class="text-cornflower-800"
                  />
                  <div
                    class="rounded h-5.5 w-11 bg-cornflower-800 flex items-center justify-center"
                  >
                    <BlTextSubtitle3
                      :text="creditsUsed"
                      class="text-cornflower-200"
                    />
                  </div>
                </div>
                <div class="flex items-center justify-between gap-2">
                  <BlTextBody2
                    text="Projected remaining credits after this session(s) is completed"
                    class="text-cornflower-800"
                  />
                  <div
                    class="rounded h-5.5 w-11 bg-cornflower-800 flex items-center justify-center"
                  >
                    <BlTextSubtitle3
                      :text="remainingCredits"
                      class="text-cornflower-200"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="packageItemIdInput && remainingCredits < 0"
            class="rounded flex items-start gap-2 py-2 px-3 bg-tulip-100"
          >
            <BlIcon icon="warning" color="tulip-7" class="h-6 w-6" />
            <div class="flex flex-col w-128">
              <BlTextBody2
                text="The credits available after this appointment will be negative. Please add credits to the client's account before creating the appointment."
                class="text-tulip-700"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="
          appointment.remaining_appointments_count > 0 &&
            isOrphanAppointment &&
            isRescheduleAsReassign
        "
        class="w-152 py-10 px-10 flex flex-col bg-pure-white rounded-3xl border border-slate-400"
      >
        <div class="mb-3 flex flex-col gap-1">
          <BlTextSubtitle1 text="Recurring details" />
          <BlTextBody2
            text="Specify whether the new therapist will apply to this appointment only or to all remaining appointments."
          />
        </div>
        <div class="flex flex-col gap-3">
          <div
            v-for="opt in reassignOptionOptions"
            :key="opt.value"
            class="h-10 px-4 flex items-center gap-2 border border-grey-200 rounded"
          >
            <BlInputRadio
              name="reassign_option"
              :checked="reassignOptionInput === opt.value"
              @changed="reassignOptionInput = opt.value"
            />
            <BlTextBody2 :text="opt.label" class="text-slate-1000" />
          </div>
        </div>
      </div>

      <div
        v-if="!isOrphanAppointment"
        class="w-152 py-10 px-10 flex flex-col bg-pure-white rounded-3xl border border-slate-400"
      >
        <div v-if="packageItemInputData" class="mb-3 flex flex-col gap-1">
          <BlTextSubtitle1 text="Reschedule to a new timeslot" />
          <div
            v-if="packageItemInputData.total_remaining_reschedule_credit === 0"
          >
            <BlTextBody2
              text="This client has no reschedule credits left and cannot change this appointment."
            />
          </div>
          <div v-else>
            <BlTextBody2 text="This client has" />
            <BlTextBody2
              :text="
                packageItemInputData.total_remaining_reschedule_credit ||
                  'unlimited'
              "
            />
            <BlTextBody2 text="reschedule credit(s) available." />
            <BlTextBody2
              text="Please select a new time slot for this appointment."
            />
          </div>
        </div>

        <div class="flex flex-col gap-4">
          <div
            class="flex items-center gap-2"
            :class="{ 'justify-center': !therapistInput }"
          >
            <div class="w-25 flex-shrink-0 rounded shadow-md">
              <div
                class="h-7 flex items-center justify-center rounded-t bg-prim-100"
              >
                <BlTextBody2 text="New" class="text-dark-purple-100" />
              </div>
              <div
                class="h-18 flex flex-col items-center justify-center rounded-b bg-pure-white"
              >
                <BlTextTitle4
                  :text="displayDate({ date: dateInput, format: 'DD' })"
                  :class="[
                    dateInput ? 'text-dark-purple-100' : 'text-transparent'
                  ]"
                />
                <BlTextBody2
                  :text="
                    displayDate({
                      date: dateInput,
                      format: 'MMMM',
                      empty: 'Unselected'
                    })
                  "
                  class="text-light-purple-400"
                />
              </div>
            </div>
            <div
              v-if="therapistInput"
              class="w-full flex items-center justify-between gap-2"
            >
              <div class="w-90 flex flex-col gap-1 px-2">
                <div
                  class="flex items-center gap-2"
                  :title="therapistInputData.name"
                >
                  <BlIcon icon="user" color="slate-6" class="h-4 w-4" />
                  <BlTextBody2
                    :text="therapistInputData.name"
                    class="truncate"
                  />
                </div>
                <div class="flex items-center gap-2">
                  <BlIcon
                    icon="clock-outline"
                    color="slate-6"
                    class="h-4 w-4"
                  />
                  <BlTextBody2 :text="`${startTimeInput} - ${endTimeInput}`" />
                </div>
                <div v-if="enableBranch" class="flex items-center gap-2">
                  <BlIcon icon="location" color="slate-6" class="h-4 w-4" />
                  <BlTextBody2
                    :text="room.branch ? room.branch.name : 'No branch'"
                  />
                </div>
                <div class="flex items-center gap-2">
                  <BlIcon icon="door" color="slate-6" class="h-4 w-4" />
                  <div
                    v-if="
                      appointment.room.id === roomInput ||
                        !isRescheduleAsReassign
                    "
                  >
                    <BlTextBody2 :text="room.name" />
                    <BlTextBody2
                      v-if="therapistInputData.is_open_room"
                      text="(open room)"
                    />
                  </div>
                  <div v-else>
                    <BlTextBody2
                      :text="`Move to ${room.name}`"
                      class="font-bold"
                    />
                  </div>
                </div>

                <div
                  v-if="!isRescheduleAsReassign"
                  class="flex items-center gap-2"
                >
                  <BlIcon icon="piggy" color="slate-6" class="h-4 w-4" />
                  <div>
                    <BlTextBody2 :text="`${newSlotCount} credit(s)`" />
                    <BlTextBody2
                      v-if="serviceData"
                      :text="`of ${serviceData.package_item_name}`"
                    />
                  </div>
                </div>
              </div>
              <BlInputButton
                type="outlined"
                icon="edit"
                @clicked="process = 'select_reschedule'"
              />
            </div>
          </div>
          <div
            v-if="isRescheduleAsReassign && process === 'reschedule'"
            class="py-2 px-3 flex gap-2 bg-cornflower-200 rounded"
          >
            <BlIcon icon="info" color="cornflower-8" class="h-6 w-6" />
            <BlTextBody2
              text="No reschedule credits will be required, as the appointment's date and timeslot remain unchanged."
              class="text-cornflower-800"
            />
          </div>
          <BlInputButton
            v-if="!therapistInput"
            label="Select timeslot"
            dense
            :disabled="
              packageItemInputData &&
                packageItemInputData.total_remaining_reschedule_credit === 0
            "
            @clicked="process = 'select_reschedule'"
          />
        </div>
      </div>

      <div
        v-if="!isRescheduleAsReassign"
        class="w-152 py-10 px-10 flex flex-col bg-pure-white rounded-3xl border border-slate-400"
      >
        <div class="mb-3 flex flex-col gap-1">
          <BlTextSubtitle1 text="Rescheduling details" />
          <BlTextBody2
            text="Please complete the following information to finalize the rescheduling:"
          />
        </div>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <BlTextBody2 text="Select who is requesting the rescheduling:" />
            <div
              v-for="opt in rescheduleRequesterTypeOptions"
              :key="opt.value"
              class="h-10 px-4 flex items-center gap-2 border border-grey-200 rounded"
            >
              <BlInputRadio
                name="reschedule_requester_type"
                :checked="rescheduleRequesterTypeInput === opt.value"
                @changed="rescheduleRequesterTypeInput = opt.value"
              />
              <BlTextBody2 :text="opt.label" class="text-slate-1000" />
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <div>
              <BlTextBody2
                text="Would you like to use 1 rescheduling credits for this appointment change?"
              />
              <span
                v-if="
                  packageItemInputData && packageItemInputData.transaction_used
                "
              >
                <BlTextBody2 :text="`(This will be deducted from`" />
                <BlTextBody2
                  :text="
                    `ID ${packageItemInputData.transaction_used.credit_transaction_id})`
                  "
                  class="font-bold"
                />
              </span>
            </div>
            <div
              v-for="opt in subtractRescheduleCreditOptions"
              :key="opt.value"
              class="h-10 px-4 flex items-center gap-2 border border-grey-200 rounded"
            >
              <BlInputRadio
                name="subtract_reschedule_credit"
                :checked="subtractRescheduleCreditInput === opt.value"
                @changed="subtractRescheduleCreditInput = opt.value"
              />
              <BlTextBody2 :text="opt.label" class="text-slate-1000" />
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <BlSelectBox
              label="Please select reason for the rescheduling."
              asterisk
              v-model="rescheduleReasonInput"
              placeholder="Select rescheduling reason"
              :options="
                centerReasonSettings.map(i => ({
                  value: i.formatted_name,
                  label: i.name
                }))
              "
              drop="up"
              @change="rescheduleReasonInput = $event"
            />
            <BlInputText
              v-if="rescheduleReasonInput === defaultReason"
              :input="otherRescheduleReasonInput"
              @changed="inputHandler"
            />
          </div>
        </div>
      </div>
    </div>

    <SelectRescheduleTimeslot
      v-if="process === 'select_reschedule'"
      :timeslotView="timeslotView"
      :timeslotViewOptions="timeslotViewOptions"
      @set-timeslot-view="timeslotView = $event"
      @cancel="process = processStacked.pop()"
      @continue="onChangeNewSchedule"
    />

    <div
      v-if="process !== 'select_reschedule'"
      class="sticky bottom-0 z-10 h-20 px-6 flex items-center justify-between flex-shrink-0 bg-pure-white border-t border-slate-400"
    >
      <BlInputButton
        type="error"
        label="Cancel appointment"
        :disabled="submitLoading"
        @clicked="cancelOpened = true"
      />
      <BlInputButton
        v-if="isRescheduleAsReassign"
        label="Update appointment"
        :disabled="isDisabledSubmit"
        :loading="submitLoading"
        @clicked="onReassign"
      />
      <BlInputButton
        v-else
        label="Reschedule appointment"
        :disabled="isDisabledSubmit"
        :loading="submitLoading"
        @clicked="onReschedule"
      />
    </div>

    <CancelAppointmentModal
      v-if="cancelOpened && currentUser.role.includes('admin')"
      :appointmentId="appointment.id"
      @close-modal="cancelOpened = false"
      @after-commit="onAfterCancelled"
    />

    <BlBackdrop
      v-if="unavailabilityDetectedOpened"
      title="Therapist unavailability detected"
      :footer="false"
    >
      <div class="px-8 mb-4 flex flex-col gap-4" :style="{ width: '40rem' }">
        <div class="flex flex-col gap-1">
          <div>
            <BlTextBody2 :text="therapistData.name" class="font-bold" />
            <BlTextBody2
              text="is unavailable on the following dates because they either have conflicting appointments or their availability is in a different room:"
            />
          </div>
          <div
            v-for="(date, idx) in conflictedDates"
            :key="idx"
            class="flex items-center gap-2 px-2"
          >
            <div class="rounded h-1 w-1 bg-slate-800"></div>
            <BlTextBody2 :text="date" />
          </div>
          <div>
            <BlTextBody2
              text="If you proceed, the therapist will only be assigned to"
            />
            <BlTextBody2 :text="incomingTotalSuccess" class="font-bold" />
            <BlTextBody2 text="appointment(s) without conflicts." />
          </div>
        </div>
      </div>

      <div class="flex items-center justify-end px-8 pb-8">
        <div class="flex items-center gap-4">
          <BlInputButton
            type="outlined"
            label="Back to edit appointment"
            @clicked="unavailabilityDetectedOpened = false"
          />
          <BlInputButton
            label="Proceed"
            :loading="submitLoading"
            @clicked="onProceedReassign"
          />
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { displayDate } from "@/plugins/helper.js";

import SelectRescheduleTimeslot from "./SelectRescheduleTimeslot.vue";
import CancelAppointmentModal from "../modal/CancelAppointmentModal.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextSubtitle4 from "../../BlTextSubtitle4.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlBreadcrumbs from "../../BlBreadcrumbs.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlInputText from "../../BlInputText.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "RescheduleAppointmentOverlay",
  components: {
    SelectRescheduleTimeslot,
    CancelAppointmentModal,
    //
    BlTextTitle4,
    BlTextSubtitle1,
    BlTextSubtitle3,
    BlTextSubtitle4,
    BlTextBody2,
    BlTextBody3,
    BlBreadcrumbs,
    BlIcon,
    BlInputButton,
    BlInputRadio,
    BlInputText,
    BlSelectBox,
    BlBackdrop
  },
  props: {
    appointmentId: { type: Number, required: true }
    // $emit('close-modal')
    // $emit('after-commit')
  },
  data() {
    return {
      timeslotView: "compact", // detailed | compact
      timeslotViewOptions: [
        {
          value: "detailed",
          label: "Detailed view",
          class: "font-bold text-light-purple"
        },
        {
          value: "compact",
          label: "Compact view",
          class: "font-bold text-light-purple"
        }
      ],
      //
      modalLoading: true,
      submitLoading: false,
      cancelOpened: false,
      //
      /** reassign, reschedule, select_reschedule */
      process: "reschedule",
      processStacked: ["reschedule"],
      // reassign
      therapistLoading: false,
      therapistOptions: [],
      therapistInput: 0,
      therapistData: null,
      roomInput: 0,
      creditLoading: false,
      clientCreditsData: null,
      packageItemIdInput: null,
      serviceData: null,
      reassignOptionInput: "current",
      //
      unavailabilityDetectedOpened: false,
      conflictedDates: [],
      incomingTotalSuccess: 0,
      // reschedule, select_reschedule
      dateInput: "",
      timeInput: [],
      startTimeInput: "",
      endTimeInput: "",
      isRescheduleOpenRoom: false,
      assignSupervisorsInput: [],
      rescheduleRequesterTypeInput: "client",
      rescheduleRequesterTypeOptions: [
        { value: "client", label: "Client" },
        { value: "therapist", label: "Therapist" },
        { value: "clinic_or_admin", label: "Admin/clinic" }
      ],
      subtractRescheduleCreditInput: true, // false | true
      subtractRescheduleCreditOptions: [
        { value: true, label: "Yes" },
        { value: false, label: "No" }
      ],
      rescheduleReasonInput: "",
      otherRescheduleReasonInput: {
        placeholder: "Provide a reason for the rescheduling.",
        asterisk: true,
        textarea: true,
        model: ""
      }
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "centerReasonSettings",
      "appointment",
      "user",
      "room"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    defaultReason() {
      const reason = this.centerReasonSettings.find(i => i.is_default);
      return reason ? reason.formatted_name : "other";
    },
    breadcrumbs() {
      let currentLabel = "Reschedule appointment";
      const action = () => this.$emit("close-modal");
      return [
        { label: "Appointments", action },
        { label: currentLabel, action, truncate: true }
      ];
    },
    currentSlotCount() {
      if (!this.appointment) return 0;
      const start = this.appointment.start_time_string;
      const end = this.appointment.end_time_string;
      return this.getSlotCount(start, end);
    },
    newSlotCount() {
      if (!this.appointment) return 0;
      const start = this.startTimeInput;
      const end = this.endTimeInput;
      return this.getSlotCount(start, end);
    },
    userAppointment() {
      const data = { id: null, name: null };
      if (!this.appointment) return data;
      return this.appointment.user || data;
    },
    isOrphanAppointment() {
      return this.userAppointment.id === null;
    },
    isRescheduleAsReassign() {
      /**
       * true   : reassign process or has a same timeslot (date, start_time, and end_time)
       * false  : reschedule process and has diff a timeslot (date, start_time, and end_time)
       */
      if (this.process === "reassign") return true;
      if (!this.appointment) return false;
      const isDateSame = this.appointment.date === this.dateInput;
      const isStartSame =
        this.appointment.start_time_string === this.startTimeInput;
      const isEndSame = this.appointment.end_time_string === this.endTimeInput;
      return isDateSame && isStartSame && isEndSame;
    },
    //
    therapistInputData() {
      if (!this.therapistInput) return null;
      const data = {
        ...this.therapistData,
        is_open_room: this.isRescheduleOpenRoom
      };
      if (this.isRescheduleAsReassign) {
        const found = this.therapistOptions.find(
          i => i.id === this.therapistInput
        );
        if (!found) return data;
        return found;
      } else {
        return data;
      }
    },
    //
    packageItemOptions() {
      const nullOption = {
        value: -1,
        label: `No credits available. Please add credits first to create an appointment.`,
        disabled: true
      };
      if (!this.clientCreditsData) return [nullOption];
      const availableItems = this.clientCreditsData.available_items || [];
      if (!availableItems.length) return [nullOption];
      const arr = [];
      availableItems.forEach(i => {
        const creditTransactions = i.credit_transactions || [];
        // const totalAvailableCredits = creditTransactions.reduce(
        //   (sum, i) => (sum += i.total_available_credits),
        //   0
        // );
        // const totalReservedCredits = creditTransactions.reduce(
        //   (sum, i) => (sum += i.total_reserved_credits),
        //   0
        // );
        const totalUnusedCredits = creditTransactions.reduce(
          (sum, i) => (sum += i.total_unused_credits),
          0
        );

        const remaining = totalUnusedCredits;
        const isAvailable = i.is_available;
        const suffix = !isAvailable
          ? "Unavailable for this therapist"
          : `${remaining} credit(s) available`;
        const disabled = !isAvailable || remaining < this.creditsUsed;

        arr.push({
          ...i,
          total_unused_credits: totalUnusedCredits,
          value: i.package_item_id,
          label: i.package_item_name,
          suffix,
          disabled
        });
      });
      return arr;
    },
    packageItemInputData() {
      if (!this.packageItemIdInput) return null;
      const found = this.packageItemOptions.find(
        i => i.value === this.packageItemIdInput
      );
      if (!found) return null;
      const arr = found.credit_transactions.map(
        i => i.remaining_reschedule_credit
      );
      const sum = arr.reduce((sum, i) => (sum += i || 0), 0);
      let totalRemainingRescheduleCredit = sum;
      if (arr.includes(null)) {
        totalRemainingRescheduleCredit = null;
      }
      let transactionUsed = found.credit_transactions.find(
        i => i.remaining_reschedule_credit === null
      );
      if (!transactionUsed) {
        transactionUsed = found.credit_transactions.find(
          i => i.remaining_reschedule_credit > 0
        );
      }
      return {
        ...found,
        total_remaining_reschedule_credit: totalRemainingRescheduleCredit,
        transaction_used: transactionUsed
      };
    },
    //
    creditsUsed() {
      return this.currentSlotCount;
    },
    remainingCredits() {
      if (!this.packageItemInputData) return 0;
      const remaining = this.packageItemInputData.total_unused_credits;
      return remaining - this.creditsUsed;
    },
    //
    reassignOptionOptions() {
      const count =
        (this.appointment && this.appointment.remaining_appointments_count) ||
        0;
      return [
        {
          value: "current",
          label: "Apply to this appointment only"
        },
        {
          value: "current_and_following",
          label: `Apply to this and all ${count} remaining appointment(s)`
        }
      ];
    },
    isDisabledSubmit() {
      if (!this.therapistInput) return true;
      if (!this.packageItemIdInput) return true;
      if (this.isRescheduleAsReassign) {
        if (!this.packageItemInputData) return true;
        if (this.packageItemInputData && this.packageItemInputData.disabled) {
          return true;
        }
        if (this.packageItemIdInput && this.remainingCredits < 0) return true;
      } else {
        if (!this.rescheduleReasonInput) return true;
        if (this.rescheduleReasonInput === this.defaultReason) {
          if (!this.otherRescheduleReasonInput.model) return true;
        }
      }
      return false;
    }
  },
  watch: {
    process(val) {
      if (val === "reassign" || val === "reschedule") {
        this.processStacked.push(val);
      }
    },
    therapistInput: {
      immediate: true,
      async handler(val) {
        const { data } = await this.getUser({ id: val });
        this.therapistData = data;
      }
    },
    roomInput: {
      immediate: true,
      handler(val) {
        this.getRoom({ id: val });
      }
    }
  },
  created() {
    this.getCenterReasonSettings();
  },
  async mounted() {
    this.modalLoading = true;

    const id = this.appointmentId;
    const { data: appointment } = await this.getAppointment({ id });
    /**
     * 1. reschedule
     * 2. orphan - reassign
     * 3. orphan - reassign -> reschedule
     */

    await this.fetchAvailbleCredits(appointment.client_id, 0);
    if (appointment.credit_transaction_services.length) {
      this.packageItemIdInput =
        appointment.credit_transaction_services[0].package_item_id;
    }

    const user = appointment.user;
    if (!user || !user.id) {
      this.roomInput = appointment.room.id;
      this.dateInput = appointment.date;
      this.startTimeInput = appointment.start_time_string;
      this.endTimeInput = appointment.end_time_string;
      this.process = "reassign";
      this.processStacked = ["reassign"];
    }

    this.modalLoading = false;
  },
  methods: {
    ...mapActions([
      "actionGet",
      "getCenterReasonSettings",
      "getAppointment",
      "getUser",
      "getRoom",
      "reassignAppointment",
      "rescheduleAppointment"
    ]),
    displayDate,
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    getSlotCount(start = "", end = "") {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    //
    async onChangeTherapist(val) {
      this.process = "reassign";
      this.therapistInput = val;
      this.clientCreditsData = null;
      if (!val) return;

      if (this.therapistInputData) {
        this.roomInput = this.therapistInputData.availability.room_id;
      }

      const clientId = this.appointment.client.id;
      this.fetchAvailbleCredits(clientId, val);
    },
    //
    async fetchTherapists(query) {
      const params = {
        url: `/api/v1/appointments/${this.appointmentId}/available_therapists`
      };
      if (query) params.url += `?query=${query}`;

      this.therapistLoading = true;
      const { success, data } = await this.actionGet(params);
      this.therapistLoading = false;
      if (!success) return;

      const appointmentRoomId =
        this.appointment && this.appointment.room && this.appointment.room.id;
      const arr = data.map(i => {
        const availability = i.availability || {};
        const changeRoom =
          availability.room_id !== appointmentRoomId
            ? `Change to ${availability.room_name}`
            : "";
        return {
          ...i,
          value: i.id,
          label: i.name,
          disabled: !i.is_available,
          suffix: !i.is_available ? "Unavailable" : changeRoom,
          is_open_room: availability.is_open_room
        };
      });
      this.therapistOptions = [...this.therapistOptions, ...arr];
    },
    async fetchAvailbleCredits(clientId, userId) {
      if (!clientId) return;
      this.clientCreditsData = null;
      const params = {
        url: `/api/v1/clients/${clientId}/credit_transaction_details?appointment_id=${this.appointmentId}`
      };
      if (userId) params.url += `&user_id=${userId}`;

      this.creditLoading = true;
      const { success, data } = await this.actionGet(params);
      this.creditLoading = false;
      if (!success) return;

      this.clientCreditsData = data;
    },
    //
    onChangeNewSchedule(slot) {
      this.therapistInput = slot.user_id;

      this.roomInput = slot.room_id;
      this.packageItemIdInput = slot.package_item_id;
      this.dateInput = slot.date;
      this.startTimeInput = slot.start_time_string;
      this.endTimeInput = slot.end_time_string;

      this.therapistData = slot.user;
      this.serviceData = slot.service_data;
      this.timeInput = slot.time;
      this.assignSupervisorsInput = slot.supervisor_ids;
      this.isRescheduleOpenRoom = slot.is_open_room;

      this.process = "reschedule";

      const clientId = this.appointment.client.id;
      this.fetchAvailbleCredits(clientId, slot.user_id);
    },
    onBackToReassign() {
      this.therapistInput = 0;

      this.roomInput = this.appointment.room.id;
      if (this.appointment.credit_transaction_services.length) {
        this.packageItemIdInput = this.appointment.credit_transaction_services[0].package_item_id;
      }
      this.dateInput = this.appointment.date;
      this.startTimeInput = this.appointment.start_time_string;
      this.endTimeInput = this.appointment.end_time_string;

      this.therapistData = null;
      this.serviceData = null;
      this.timeInput = [];
      this.assignSupervisorsInput = [];
      this.isRescheduleOpenRoom = false;

      this.process = "reassign";
    },
    async onReassign() {
      const reassign = {
        id: this.appointment.id,
        user_id: this.therapistInput,
        room_id: this.roomInput,
        package_item_id: this.packageItemIdInput,
        reassign_option: this.reassignOptionInput
      };

      const params = {
        id: this.appointment.id,
        client_name: this.appointment.client.name,
        params: { check: true, ...reassign }
      };

      this.conflictedDates = [];
      this.incomingTotalSuccess = 0;

      this.submitLoading = true;
      const {
        success,
        conflicted_dates: dates,
        total_success: total
      } = await this.reassignAppointment(params);

      if (!success) {
        this.submitLoading = false;
        if (dates.length || total) {
          const format = "DD MMM YYYY";
          this.conflictedDates = dates.map(date =>
            this.displayDate({ date, format })
          );
          this.incomingTotalSuccess = total;
          this.unavailabilityDetectedOpened = true;
        }
        return;
      }

      this.onProceedReassign();
    },
    async onProceedReassign() {
      const reassign = {
        id: this.appointment.id,
        user_id: this.therapistInput,
        room_id: this.roomInput,
        package_item_id: this.packageItemIdInput,
        reassign_option: this.reassignOptionInput
      };

      const params = {
        id: this.appointment.id,
        client_name: this.appointment.client.name,
        params: { ...reassign }
      };

      this.submitLoading = true;
      const {
        success,
        conflicted_dates: dates,
        total_success: total
      } = await this.reassignAppointment(params);

      if (!success) {
        this.submitLoading = false;
        if (dates.length || total) {
          const format = "DD MMM YYYY";
          this.conflictedDates = dates.map(date =>
            this.displayDate({ date, format })
          );
          this.incomingTotalSuccess = total;
          this.unavailabilityDetectedOpened = true;
        }
        return;
      }

      setTimeout(() => {
        this.submitLoading = false;
        this.$emit("after-commit");
        this.$emit("close-modal");
      }, 1000);
    },
    async onReschedule() {
      let requesterType;
      let requesterId;
      const rescheduledBy = this.rescheduleRequesterTypeInput;
      switch (rescheduledBy) {
        case "client":
          requesterType = "Client";
          const clientId = this.appointment.client.id;
          requesterId = clientId;
          break;
        case "therapist":
          requesterType = "User";
          const userId = this.therapistInput;
          requesterId = userId;
          break;
        case "clinic_or_admin":
          requesterType = "User";
          requesterId = this.currentUser.id;
          break;
        default:
          requesterType = "";
          requesterId = "";
          break;
      }

      const params = {
        id: this.appointment.id,
        client_name: this.appointment.client.name,
        params: {
          id: this.appointment.id,
          date: this.dateInput,
          user_id: this.therapistInput,
          room_id: this.roomInput,
          start_time: this.startTimeInput,
          end_time: this.endTimeInput,
          //
          package_item_id: this.packageItemIdInput,
          //
          reschedule_requester_type: requesterType,
          reschedule_requester_id: requesterId,
          subtract_reschedule_credit: this.subtractRescheduleCreditInput,
          reschedule_reason: this.rescheduleReasonInput,
          other_reschedule_reason: ""
        }
      };

      if (this.rescheduleReasonInput === this.defaultReason) {
        params.params.other_reschedule_reason = this.otherRescheduleReasonInput.model;
      }

      this.submitLoading = true;
      const { success } = await this.rescheduleAppointment(params);
      if (!success) {
        this.submitLoading = false;
        return;
      }

      setTimeout(() => {
        this.submitLoading = false;
        this.$emit("after-commit");
        this.$emit("close-modal");
      }, 1000);
    },
    onAfterCancelled() {
      this.cancelOpened = false;
      this.$emit("after-commit");
      this.$emit("close-modal");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/overlay/RescheduleAppointmentOverlay.vue